/**
 * Hook para gerenciar configurações visuais do chat público do fluxo
 * Operações CRUD para flow_chat_configs
 */

import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { showToast } from '@/lib/toast';
import { logger } from '@/lib/logger';

// Interface de configuração do chat com suporte a templates
export interface FlowChatConfig {
  id?: string;
  flow_id: string;
  tenant_id: string;
  primary_color: string;
  background_color: string;
  bot_avatar_enabled: boolean; // Controla exibição do avatar (se true sem URL, mostra ícone padrão)
  bot_avatar_url: string | null;
  user_avatar_enabled: boolean;
  show_branding: boolean;
  custom_css: string | null;
  company_name: string | null;
  company_logo_url: string | null;
  max_width: string;
  // Campos estendidos para templates
  template_id: string | null;
  header_bg_color: string;
  header_text_color: string;
  user_bubble_color: string;
  bot_bubble_color: string;
  user_text_color: string;
  bot_text_color: string;
  border_radius: string;
  // Campo para imagem de fundo (WhatsApp, etc.)
  background_image: string | null;
}

// Valores padrão (template UaizyChat - verde água)
const DEFAULT_CONFIG: Omit<FlowChatConfig, 'flow_id' | 'tenant_id'> = {
  primary_color: '#5BBFBA',
  background_color: '#ffffff',
  bot_avatar_enabled: false,
  bot_avatar_url: null,
  user_avatar_enabled: true,
  show_branding: true,
  custom_css: null,
  company_name: null,
  company_logo_url: null,
  max_width: '800px',
  // Valores padrão UaizyChat (verde água)
  template_id: 'uaizychat',
  header_bg_color: '#5BBFBA',
  header_text_color: '#ffffff',
  user_bubble_color: '#5BBFBA',
  bot_bubble_color: '#FFFFFF',
  user_text_color: '#ffffff',
  bot_text_color: '#1f2937',
  border_radius: '16px',
  background_image: null,
};

/**
 * Hook para gerenciar configurações do chat público
 */
export function useFlowChatConfig(flowId: string | undefined) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const [localConfig, setLocalConfig] = useState<FlowChatConfig | null>(null);

  // Query para buscar configuração existente
  const { data: config, isLoading, refetch } = useQuery({
    queryKey: ['flow-chat-config', flowId],
    queryFn: async () => {
      if (!flowId) return null;

      const { data, error } = await supabase
        .from('flow_chat_configs')
        .select('*')
        .eq('flow_id', flowId)
        .maybeSingle();

      if (error) {
        logger.error('[useFlowChatConfig] Erro ao buscar config:', error.message);
        throw error;
      }

      return data as FlowChatConfig | null;
    },
    enabled: !!flowId,
  });

  // Inicializa config local quando dados carregam
  // Se não houver template selecionado (template_id null), aplica UaizyChat como padrão
  useEffect(() => {
    if (config) {
      // Se config existe mas não tem template_id, aplica valores do UaizyChat
      if (!config.template_id) {
        setLocalConfig({
          ...config,
          template_id: 'uaizychat',
          primary_color: config.primary_color || DEFAULT_CONFIG.primary_color,
          header_bg_color: config.header_bg_color || DEFAULT_CONFIG.header_bg_color,
          header_text_color: config.header_text_color || DEFAULT_CONFIG.header_text_color,
          user_bubble_color: config.user_bubble_color || DEFAULT_CONFIG.user_bubble_color,
          bot_bubble_color: config.bot_bubble_color || DEFAULT_CONFIG.bot_bubble_color,
          user_text_color: config.user_text_color || DEFAULT_CONFIG.user_text_color,
          bot_text_color: config.bot_text_color || DEFAULT_CONFIG.bot_text_color,
          border_radius: config.border_radius || DEFAULT_CONFIG.border_radius,
        });
      } else {
        setLocalConfig(config);
      }
    } else if (flowId && currentTenant?.id) {
      // Config padrão se não existir - sempre UaizyChat
      setLocalConfig({
        ...DEFAULT_CONFIG,
        flow_id: flowId,
        tenant_id: currentTenant.id,
      });
    }
  }, [config, flowId, currentTenant?.id]);

  // Mutation para salvar configuração
  const saveMutation = useMutation({
    mutationFn: async (configData: Partial<FlowChatConfig>) => {
      if (!flowId || !currentTenant?.id) {
        throw new Error('Flow ID e Tenant ID são obrigatórios');
      }

      const dataToSave = {
        ...DEFAULT_CONFIG,
        ...configData,
        flow_id: flowId,
        tenant_id: currentTenant.id,
      };

      // Upsert - insere ou atualiza
      const { data, error } = await supabase
        .from('flow_chat_configs')
        .upsert(dataToSave, { onConflict: 'flow_id' })
        .select()
        .single();

      if (error) {
        logger.error('[useFlowChatConfig] Erro ao salvar config:', error.message);
        throw error;
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['flow-chat-config', flowId] });
      setLocalConfig(data);
      // Toast removido - componente FlowTheme.tsx controla feedback ao usuário
    },
    onError: (error: Error) => {
      // Toast removido - componente FlowTheme.tsx controla feedback ao usuário
      logger.error('[useFlowChatConfig] Erro:', error.message);
    },
  });

  // Atualiza campo específico no estado local
  const updateField = useCallback(<K extends keyof FlowChatConfig>(
    field: K,
    value: FlowChatConfig[K]
  ) => {
    setLocalConfig((prev) => {
      if (!prev) return prev;
      return { ...prev, [field]: value };
    });
  }, []);

  // Salva configurações
  const saveConfig = useCallback(() => {
    if (localConfig) {
      saveMutation.mutate(localConfig);
    }
  }, [localConfig, saveMutation]);

  // Reset para valores padrão
  const resetToDefaults = useCallback(() => {
    if (flowId && currentTenant?.id) {
      setLocalConfig({
        ...DEFAULT_CONFIG,
        flow_id: flowId,
        tenant_id: currentTenant.id,
      });
    }
  }, [flowId, currentTenant?.id]);

  return {
    config: localConfig,
    isLoading,
    isSaving: saveMutation.isPending,
    updateField,
    saveConfig,
    resetToDefaults,
    refetch,
  };
}
